import React, { useEffect, useState } from 'react';

import {
  buscarEventosDaPlaca,
  EventosDaPlaca,
} from '../../../../api/contac/contacRecomendacoesApi';
import { notify } from '../../../../shared/feedback/notify';
import '../../../../shared/feedback/erro-de-tela.css';
import './BlocoEventosDaPlaca.css';

interface Props {
  placa: string;
}

/**
 * "Por que este valor" — os eventos do SGA da placa na janela analisada.
 *
 * ## Os três estados existem porque lista vazia significa coisas diferentes
 *
 * Esta é a razão de o componente não ser uma tabela simples. Medido em 01/09/2026: das 280 placas
 * de RJ no vermelho, apenas 8 têm evento — ou seja, **"sem evento" é o caso majoritário**, e é
 * informação valiosa (o prejuízo é estrutural, não foi uma batida). Já para placa de outra base o
 * espelho não tem dado nenhum, e ali o sistema **não sabe**.
 *
 * Mostrar os dois casos como uma tabela vazia faria o usuário ler "não houve evento" onde a
 * resposta certa é "não temos como verificar" — a regra da at-60 aplicada a dado ausente.
 *
 * Falha de carga também não vira lista vazia: aparece a faixa de erro.
 */
const BlocoEventosDaPlaca: React.FC<Props> = ({ placa }) => {
  const [dados, setDados] = useState<EventosDaPlaca | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(false);

  useEffect(() => {
    let ativo = true;
    setCarregando(true);
    setErro(false);

    (async () => {
      try {
        const resposta = await buscarEventosDaPlaca(placa);
        if (ativo) setDados(resposta);
      } catch (error) {
        if (!ativo) return;
        setDados(null);
        setErro(true);
        notify.errorFrom(
          error,
          'Não foi possível carregar os eventos desta placa. Recarregue a página; se persistir, acione a TI.',
        );
      } finally {
        if (ativo) setCarregando(false);
      }
    })();

    return () => {
      ativo = false;
    };
  }, [placa]);

  return (
    <div className="card">
      <div className="modal-header">Eventos no período</div>

      {carregando && <div className="card-entry">Carregando os eventos...</div>}

      {!carregando && erro && (
        <p className="erro-de-tela">
          Não foi possível verificar os eventos desta placa. Os valores acima continuam válidos; o
          que falhou foi esta consulta.
        </p>
      )}

      {!carregando && !erro && dados && (
        <>
          {dados.cobertura === 'base-sem-espelho' && (
            <div className="dre-aviso-sem-cadastro">
              Não é possível verificar eventos desta placa
              {dados.baseTag ? <> (base <strong>{dados.baseTag}</strong>)</> : null}: a consulta de
              eventos do SGA está disponível apenas para a base do Rio de Janeiro. Isso{' '}
              <strong>não significa que a placa não teve evento</strong> — significa que o sistema
              não tem como saber.
            </div>
          )}

          {dados.cobertura === 'sem-evento-no-periodo' && (
            <div className="bloco-eventos-vazio">
              <p>
                <strong>Nenhum evento registrado</strong> para esta placa entre{' '}
                {dados.competenciaInicio} e {dados.competenciaBase}.
              </p>
              <p className="bloco-eventos-apoio">
                O resultado negativo, portanto, não é explicado por colisão, roubo ou furto — é
                prejuízo recorrente da própria operação da placa.
              </p>
            </div>
          )}

          {dados.cobertura === 'com-eventos' && (
            <>
              <p className="bloco-eventos-apoio">
                O prejuízo do período pode ser explicado, no todo ou em parte, por{' '}
                {dados.eventos.length === 1
                  ? 'este evento'
                  : `estes ${dados.eventos.length} eventos`}
                . O custo de um evento é o funcionamento normal da proteção veicular.
              </p>
              <div className="do-tabela-wrapper">
                <table className="do-tabela">
                  <thead>
                    <tr>
                      <th>Data</th>
                      <th>Motivo</th>
                      <th>Envolvimento</th>
                      <th>Situação</th>
                      <th>Participação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dados.eventos.map((evento, indice) => (
                      <tr key={evento.codigoEvento ?? indice}>
                        <td title={evento.dataEvento}>{evento.dataEvento || '-'}</td>
                        <td title={evento.motivo}>{evento.motivo}</td>
                        <td title={evento.envolvimento}>{evento.envolvimento}</td>
                        <td title={evento.situacao}>{evento.situacao || '-'}</td>
                        <td title={formatarMoeda(evento.participacao)}>
                          {formatarMoeda(evento.participacao)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

function formatarMoeda(valor: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor || 0);
}

export default BlocoEventosDaPlaca;
